import React, { useState } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioQuestion from "../../components/RadioQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import TextQuestion from "../../components/TextQuestion";
import TrustpilotBanner from "../../components/TrustpilotBanner";
import Banner from "../../components/Banner";
import Question from "../../components/Question";
import { formatMoneyWithCurrency, normaliseMoney, isMoneyValid } from "../../shared/utils/moneyUtils";
import { parseTenure } from "../../utils/queryStringUtils";
import RadioConditional from "../../components/RadioConditional";
import { TENURE_OPTIONS, YES_NO_OPTIONS, INSURANCE_LIMIT, BUYING_REJECTION_OPTIONS } from "../../constants/index";
import QuoteLandingPageCopy from "../Quote/QuoteLandingPageCopy";
import ReferralCodeControl, { referrerCodeCopy } from "./ReferralCodeControl";
import ReferralCodeBanner from "../../components/ReferralCodeBanner";
import { isConnellsReferral } from "../ReferrerQuote/utils/ReferrerQuoteUtils";
import { getQuoteEnabled } from "./utils/api";
import AnyOfTheseQuestion from "../../components/AnyOfTheseQuestion";

// Buying land that isn’t registered and buying at auction come from sign-up’s list, so both ask them the same way
const CHECKLIST_OPTIONS = [
  { name: "newBuild", title: "Buying a new build, or a building recently converted into flats" },
  { name: "buyToLet", title: "Buy-to-let (the property will be rented out)" },
  {
    name: "sharedOwnership",
    title:
      "Buying through shared ownership: part buy, part rent from a housing association (buying with a partner doesn’t count)",
  },
  { name: "clientIsCompany", title: "Buying through a company" },
  { name: "retirementProperty", title: "Buying a retirement or later-living home, such as sheltered housing" },
  ...BUYING_REJECTION_OPTIONS,
];

const QuoteBuyingProperty = (props) => {
  const [fields, setFields] = useState(() => {
    if (props.buyingDetails) {
      const bd = props.buyingDetails;
      return {
        price: formatMoneyWithCurrency(bd.price),
        tenure: bd.tenure,
        firstTimeBuyer: props.transactionType === "BuyingAndSelling" ? false : bd.firstTimeBuyer,
        additionalProperty: bd.additionalProperty,
        buyToLet: bd.buyToLet,
        clientIsCompany: bd.clientIsCompany,
        hasMortgage: bd.hasMortgage,
        sharedOwnership: bd.sharedOwnership,
        newBuild: bd.newBuild,
        retirementProperty: bd.retirementProperty,
        unregisteredLand: bd.unregisteredLand,
        auction: bd.auction,
      };
    } else {
      return {
        price: "",
        tenure: parseTenure(),
        firstTimeBuyer: props.transactionType === "BuyingAndSelling" ? false : null,
        additionalProperty: null,
        buyToLet: null,
        clientIsCompany: null,
        hasMortgage: null,
        sharedOwnership: null,
        newBuild: null,
        retirementProperty: null,
        unregisteredLand: null,
        auction: null,
      };
    }
  });
  const setField = (name) => (value) => {
    if (name === "firstTimeBuyer" && value) {
      setFields((prev) => ({
        ...prev,
        firstTimeBuyer: true,
        additionalProperty: false,
      }));
    } else if (name === "additionalProperty" && value) {
      setFields((prev) => ({
        ...prev,
        firstTimeBuyer: false,
        additionalProperty: true,
      }));
    } else {
      setFields((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const [errors, setErrors] = useState({});
  const setError = (field, isError) => {
    setErrors((prev) => ({
      ...prev,
      [field]: isError,
    }));
  };

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [referral, setReferral] = useState(props.referral);

  const showMortgageQuestion = !!props.referral && isConnellsReferral(props.referral.referralSource);

  const handleValidation = () => {
    let newErrors = {};
    let formIsValid = true;

    if (fields["price"] == null || !isMoneyValid(fields["price"])) {
      newErrors["price"] = true;
      formIsValid = false;
    }

    if (fields["tenure"] == null) {
      newErrors["tenure"] = true;
      formIsValid = false;
    }

    // For buying, if not a first time buyer - require additional property
    if (props.transactionType === "Buying") {
      if (fields["firstTimeBuyer"] == null) {
        newErrors["firstTimeBuyer"] = true;
        formIsValid = false;
      }

      if (fields["firstTimeBuyer"] === false) {
        if (fields["additionalProperty"] == null) {
          newErrors["additionalProperty"] = true;
          formIsValid = false;
        }
      }
    }

    // For buying and selling, can't be a first time buyer - require additional property
    if (props.transactionType === "BuyingAndSelling") {
      if (fields["additionalProperty"] == null) {
        newErrors["additionalProperty"] = true;
        formIsValid = false;
      }
    }

    if (showMortgageQuestion && fields["hasMortgage"] == null) {
      newErrors["hasMortgage"] = true;
      formIsValid = false;
    }

    if (CHECKLIST_OPTIONS.some((option) => fields[option.name] == null)) {
      newErrors["checklist"] = true;
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    if (event.type !== "blur") {
      setError(field, false);
    } else {
      if (!fields[field]) {
        setError(field, true);
      }
    }
  };

  const handlePriceFieldValidation = (event) => {
    if (event.type !== "blur") {
      setError("price", false);
    } else {
      if (!fields.price || !isMoneyValid(fields.price)) {
        setError("price", true);
      }
    }
  };

  const loadRejectionReasons = (onLoad) => {
    let reasons = [];

    if (normaliseMoney(fields.price) >= INSURANCE_LIMIT) {
      reasons.push("price_too_high");
    }

    if (!!fields.unregisteredLand) {
      reasons.push("unregistered_land");
    }
    if (!!fields.auction) {
      reasons.push("auction");
    }
    if (!!fields.newBuild) {
      reasons.push("new_build");
    }
    if (!!fields.retirementProperty) {
      reasons.push("retirement_property");
    }
    if (!!fields.clientIsCompany) {
      reasons.push("company");
    }
    if (!!fields.sharedOwnership) {
      reasons.push("shared_ownership");
    }

    if (reasons.length > 0) {
      // Skip loading additional reasons if we already have some
      onLoad(reasons);
      return;
    }

    setLoading(true);
    getQuoteEnabled({
      source: referral?.referralSource || props.defaultChannel,
      transactionType: "buying",
      tenure: fields.tenure,
      onLoaded: (enabled, additionalReasons) => {
        if (!enabled) {
          reasons.push(...additionalReasons);
        }
        setLoading(false);
        onLoad(reasons);
      },
      onError: (err) => {
        setLoadError("Unable to generate your quote");
        setLoading(false);
      },
    });
  };

  const saveSelection = () => {
    if (!handleValidation()) {
      return;
    }
    loadRejectionReasons((reasons) => {
      props.onStepComplete({
        buyingDetails: {
          price: normaliseMoney(fields.price),
          tenure: fields.tenure,
          firstTimeBuyer: fields.firstTimeBuyer,
          additionalProperty: fields.additionalProperty,
          newBuild: fields.newBuild,
          buyToLet: fields.buyToLet,
          clientIsCompany: fields.clientIsCompany,
          hasMortgage: fields.hasMortgage,
          sharedOwnership: fields.sharedOwnership,
          retirementProperty: fields.retirementProperty,
          unregisteredLand: fields.unregisteredLand,
          auction: fields.auction,
        },
        buyingRejectionReasons: reasons,
        referral: referral,
      });
    });
  };

  if (!!loadError) {
    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title="Sorry, that didn't work" removeTopPadding>
          {loadError}
        </Card>
      </React.Fragment>
    );
  }

  const isDirect = props.mode === "direct";

  let cardTitle;
  let priceHint;
  let firstTimeBuyerQuestion;
  let firstTimeBuyerHint;
  let additionalPropertyQuestion;
  let additionalPropertyHint;
  let buyToLetBannerText;
  let buttonText;

  if (isDirect) {
    cardTitle =
      props.transactionType === "BuyingAndSelling" ? "Now the home you’re buying" : "About the home you’re buying";
    priceHint = "An estimate is fine. You can change it later.";
    firstTimeBuyerQuestion = "Are you a first-time buyer?";
    firstTimeBuyerHint =
      "If you’ve never owned a home, anywhere in the world, you’re a first-time buyer and may pay less Stamp Duty";
    additionalPropertyQuestion = "Is this on top of a home you already own?";
    additionalPropertyHint =
      props.transactionType === "BuyingAndSelling"
        ? "Answer Yes only if you’ll still own another home once this sale and purchase complete"
        : "Any other home counts, including one you rent out. Answer No if you’re selling your current home at the same time";
    buyToLetBannerText =
      "First-time buyers don’t get Stamp Duty relief on a buy-to-let, so your quote uses the standard rates.";
    // A buy-to-let answer routes to the lender step first, so only promise the details screen otherwise
    buttonText = fields.buyToLet ? "Continue" : "Next: your details";
  } else {
    cardTitle =
      props.transactionType === "BuyingAndSelling"
        ? "Now the home your client is buying"
        : "About the home your client is buying";
    priceHint = "The agreed price, or the asking price if nothing’s agreed. An estimate is fine.";
    firstTimeBuyerQuestion = "Are they a first-time buyer?";
    firstTimeBuyerHint =
      "If they’ve never owned a home, anywhere in the world, they’re a first-time buyer and may pay less Stamp Duty";
    additionalPropertyQuestion = "Is this on top of a home they already own?";
    additionalPropertyHint =
      props.transactionType === "BuyingAndSelling"
        ? "Answer Yes only if they’ll still own another home once the sale and purchase complete"
        : "Any other home counts, including one they rent out. Answer No if they’re selling their current home at the same time";
    buyToLetBannerText =
      "First-time buyers don’t get Stamp Duty relief on a buy-to-let, so the quote uses standard rates.";
    buttonText = "Continue";
  }

  return (
    <React.Fragment>
      <ScrollToTop />

      {props.showLandingCopy && <QuoteLandingPageCopy pageWidth={props.pageWidth} />}

      <Card title={cardTitle} removeTopPadding={true}>
        <TextQuestion
          question="Purchase price"
          hint={priceHint}
          inputMode="numeric"
          value={fields.price}
          valueEntered={setField("price")}
          validate={handlePriceFieldValidation}
          validationError={errors["price"] ? "Enter the purchase price, for example 350000" : null}
        />

        <RadioQuestion
          question="Is the property freehold or leasehold?"
          options={TENURE_OPTIONS}
          name="tenure"
          selected={fields.tenure}
          optionSelected={setField("tenure")}
          validate={handleFieldValidation("tenure")}
          validationError={errors["tenure"] ? "Choose freehold or leasehold" : null}
        />

        {props.transactionType === "Buying" && (
          <React.Fragment>
            <RadioQuestion
              question={firstTimeBuyerQuestion}
              hint={firstTimeBuyerHint}
              options={YES_NO_OPTIONS}
              inline={true}
              name="firstTimeBuyer"
              selected={fields.firstTimeBuyer}
              optionSelected={setField("firstTimeBuyer")}
              validate={handleFieldValidation("firstTimeBuyer")}
              validationError={errors["firstTimeBuyer"] ? "Choose Yes or No" : null}
            />
            {fields.firstTimeBuyer === false && (
              <RadioConditional>
                <RadioQuestion
                  question={additionalPropertyQuestion}
                  hint={additionalPropertyHint}
                  options={YES_NO_OPTIONS}
                  inline={true}
                  name="additionalProperty"
                  selected={fields.additionalProperty}
                  optionSelected={setField("additionalProperty")}
                  validate={handleFieldValidation("additionalProperty")}
                  validationError={errors["additionalProperty"] ? "Choose Yes or No" : null}
                />
              </RadioConditional>
            )}
          </React.Fragment>
        )}

        {props.transactionType === "BuyingAndSelling" && (
          <RadioQuestion
            question={additionalPropertyQuestion}
            hint={additionalPropertyHint}
            options={YES_NO_OPTIONS}
            inline={true}
            name="additionalProperty"
            selected={fields.additionalProperty}
            optionSelected={setField("additionalProperty")}
            validate={handleFieldValidation("additionalProperty")}
            validationError={errors["additionalProperty"] ? "Choose Yes or No" : null}
          />
        )}

        {showMortgageQuestion && (
          <RadioQuestion
            question="Are they getting a mortgage?"
            options={YES_NO_OPTIONS}
            inline={true}
            name="hasMortgage"
            selected={fields.hasMortgage}
            optionSelected={setField("hasMortgage")}
            validate={handleFieldValidation("hasMortgage")}
            validationError={errors["hasMortgage"] ? "Choose Yes or No" : null}
          />
        )}

        <AnyOfTheseQuestion
          options={CHECKLIST_OPTIONS}
          fields={fields}
          onChange={(answers) => {
            setFields((prev) => ({ ...prev, ...answers }));
            setError("checklist", false);
          }}
          invalid={!!errors["checklist"]}
        />

        {fields.firstTimeBuyer && fields.buyToLet && (
          <Question>
            <Banner variant="attention" border="outline">
              {buyToLetBannerText}
            </Banner>
          </Question>
        )}

        {!!props.referral && !props.hideReferralCodeBanner && (
          <ReferralCodeBanner text={props.referral.discountMessage} small={true} />
        )}

        {!props.referralLocked && (
          <ReferralCodeControl
            audience={props.mode === "referrer" ? "referrer" : null}
            {...(props.mode === "referrer" ? referrerCodeCopy : {})}
            referral={referral}
            onReferralCodeApplied={setReferral}
          />
        )}

        <Action onClick={saveSelection} disabled={loading}>
          {buttonText}
        </Action>
      </Card>
      <TrustpilotBanner pageWidth={props.pageWidth} />
    </React.Fragment>
  );
};

export default QuoteBuyingProperty;
